import { useEffect, useMemo, useState } from "react"

// A patent-specific equivalent of ProgressBar_v2 (that component's steps
// are hardcoded for the Design page, not actually generic). Steps here
// are expected to grow as later tickets add sections (Titel, Priorität,
// Unterlagen, Signature) — each new section should add its own
// {label, icon, target} entry here plus a matching id="target" wrapper.
const STEPS = [
  { label: "Applicant & Inventors", icon: "/anmeldervertreter.svg", target: "applicant" },
  { label: "Signature", icon: "/unterschrift.svg", target: "signature" },
  { label: "Payment", icon: "/zahlungsverfahren.svg", target: "payment" }
]

export default function PatentProgressBar({
  complete
}: {
  complete: Record<string, boolean>
}) {
  const sections = useMemo(() => STEPS, [])
  const [hoveredSection, setHoveredSection] = useState<string | null>(null)

  useEffect(() => {
    const cleanups: Array<() => void> = []

    sections.forEach(({ target }) => {
      const element = document.getElementById(target)
      if (!element) return

      const enter = () => setHoveredSection(target)
      const leave = () =>
        setHoveredSection((curr) => (curr === target ? null : curr))

      element.addEventListener("mouseenter", enter)
      element.addEventListener("mouseleave", leave)

      cleanups.push(() => {
        element.removeEventListener("mouseenter", enter)
        element.removeEventListener("mouseleave", leave)
      })
    })

    return () => {
      cleanups.forEach((fn) => fn())
    }
  }, [sections])

  const handleScroll = (targetId: string) => {
    const element = document.getElementById(targetId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <div className="flex justify-between items-center w-full px-10 mt-4">
      {sections.map(({ label, icon, target }) => {
        const isHover = hoveredSection === target
        const isComplete = Boolean(complete[target])

        return (
          <div
            key={target}
            onClick={() => handleScroll(target)}
            onMouseEnter={() => setHoveredSection(target)}
            onMouseLeave={() =>
              setHoveredSection((curr) => (curr === target ? null : curr))
            }
            className={`inline-flex items-center gap-2 h-9 px-3 rounded-md cursor-pointer select-none
              transition-colors duration-200 ease-in-out
              ${
                isHover
                  ? "bg-green-100"
                  : "text-gray-700 hover:bg-green-50 hover:text-green-700 active:bg-green-200"
              }`}
          >
            {isComplete ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 shrink-0 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <img
                src={icon}
                alt={label}
                className="w-5 h-5 shrink-0 brightness-0"
              />
            )}

            <span className="text-sm font-medium leading-none">{label}</span>
          </div>
        )
      })}
    </div>
  )
}
